/** Default print-mode → pretreat Yes/No (Girl Tribe barcode + batch filter). */
export const DEFAULT_PT_INDICATION = {
  "Color Ink Only": "No",
  "Black Ink Only": "No",
  "Color+White Ink": "Yes",
  "White Ink Only": "Yes",
  "No Black Ink": "Yes",
};

/** @deprecated use DEFAULT_PT_INDICATION — kept for existing imports */
export const PT_INDICATION = DEFAULT_PT_INDICATION;

export const DTG_PRETREAT_RULES_SETTINGS_ID = "dtg-pretreat-rules";

/** Must match PrintingInfo/main.py GIRLTRIBECO. */
export const GIRLTRIBE_ENTERPRISE_ID = "oswfF8oIPavNUJxztcIc";

export const PRETREAT_FILTER_ID = "Pretreat";

export const PRETREAT_FILTER_VALUES = ["Yes", "No"];

/** DTG decoration method id — pretreat applies to DTG only. */
export const DTG_DECORATION_TYPE = "1";

/** Core modes always shown in the settings editor. */
export const CORE_PRINT_MODES = Object.keys(DEFAULT_PT_INDICATION);

/** Matches PrintingInfo WHITE_COLORS — these skip pretreat (Color Ink Only). */
const WHITE_COLORS = new Set(["white", "solid white blend"]);

/** Non-garment / size-as-color values — do not infer a print mode. */
const NON_GARMENT_COLORS = new Set([
  "",
  "osfa",
  "one size",
  "onesize",
  "default",
  "n/a",
  "na",
]);

const COLOR_INK_ONLY = "Color Ink Only";

/** Girl Tribe hardcoded CMYK (no pretreat) — mirrors girlTribeDtgPrintModes / PrintingInfo. */
const GT_SSF260_COLORS = ["Natural", "Cactus"];
const GT_STYLE_COLOR_INK_ONLY = {
  blankIds: [
    "ABJHA030",
    "SS1717",
    "SS3001Y",
    "SSOAD108",
    "ABOAD108",
    "AS5103",
    "PENCARRIEBL1201",
  ],
  colors: [
    "VANILLA MLKSHAKE",
    "ARCTIC WHITE",
    "BABY PINK",
    "ASH",
    "Ivory",
    "Sandstone",
    "Natural",
    "Cream",
    "White/ Red",
    "White/ Kelly",
  ],
};

const normalizeColor = (color) => `${color || ""}`.trim().toLowerCase();

export const getPretreatLabel = (value) =>
  value === "Yes" ? "Needs pretreat" : value === "No" ? "No pretreat" : value;

export const normalizePretreatByPrintMode = (raw) => {
  const out = { ...DEFAULT_PT_INDICATION };
  if (raw && typeof raw === "object") {
    Object.entries(raw).forEach(([mode, value]) => {
      const key = String(mode || "").trim();
      if (!key) return;
      if (value === "Yes" || value === "No") out[key] = value;
    });
  }
  return out;
};

/**
 * Normalize enterprise pretreat-rules settings.
 * Girl Tribe defaults `enabled: true` when no doc exists; everyone else `false`.
 */
export const normalizePretreatRules = (raw, { enterpriseId } = {}) => {
  const isGirlTribe = enterpriseId === GIRLTRIBE_ENTERPRISE_ID;
  const hasDoc =
    raw && typeof raw === "object" && Object.keys(raw).length > 0;
  const enabled = hasDoc ? Boolean(raw.enabled) : isGirlTribe;
  return {
    id: DTG_PRETREAT_RULES_SETTINGS_ID,
    enabled,
    pretreatByPrintMode: normalizePretreatByPrintMode(
      hasDoc ? raw.pretreatByPrintMode : null
    ),
  };
};

export const DEFAULT_PRETREAT_RULES = normalizePretreatRules(null, {});

export const printModeToPretreat = (mode, map = DEFAULT_PT_INDICATION) => {
  if (!mode) return null;
  return map?.[mode] || DEFAULT_PT_INDICATION[mode] || null;
};

/** Girl Tribe auto-tier CMYK defaults (no pretreat). */
export const getGirlTribeHardcodedPrintMode = (blankProductId, colorName) => {
  if (!blankProductId || !colorName) return null;
  const colorKey = normalizeColor(colorName);

  if (blankProductId === "SSF260") {
    if (GT_SSF260_COLORS.some((c) => normalizeColor(c) === colorKey)) {
      return COLOR_INK_ONLY;
    }
  }

  if (GT_STYLE_COLOR_INK_ONLY.blankIds.includes(blankProductId)) {
    if (
      GT_STYLE_COLOR_INK_ONLY.colors.some(
        (c) => normalizeColor(c) === colorKey
      )
    ) {
      return COLOR_INK_ONLY;
    }
  }

  return null;
};

/**
 * Infer print mode when items were never stamped with blankVariant.printMode.
 * Mirrors PrintingInfo auto rules closely enough for batch filtering.
 */
export const inferPrintModeFromItem = (item, { enterpriseId } = {}) => {
  const blankId =
    item?.blankProduct?.id ||
    item?.blankVariant?.blankProductId ||
    item?.blankProductId ||
    null;
  const color = item?.blankVariant?.color || item?.blankVariant?.variation || "";
  const colorKey = normalizeColor(color);

  if (enterpriseId === GIRLTRIBE_ENTERPRISE_ID || !enterpriseId) {
    const gt = getGirlTribeHardcodedPrintMode(blankId, color);
    if (gt) return gt;
  }

  if (!colorKey || NON_GARMENT_COLORS.has(colorKey)) return null;

  if (WHITE_COLORS.has(colorKey)) return COLOR_INK_ONLY;
  if (colorKey === "black") return "No Black Ink";
  if (colorKey.includes("black")) return "Color+White Ink";

  // Colored / heather garments typically need white underbase → pretreat.
  return "Color+White Ink";
};

/**
 * Resolve print mode for pretreat Yes/No.
 * Priority: printing_info placement → stamped blankVariant → inferred fallbacks.
 */
export const resolveItemPrintMode = (
  item,
  imageInfoEntry,
  { enterpriseId } = {}
) =>
  imageInfoEntry?.print_mode ||
  imageInfoEntry?.printMode ||
  item?.blankVariant?.printMode ||
  item?.blankVariant?.print_mode ||
  inferPrintModeFromItem(item, { enterpriseId }) ||
  null;

export const getSelectedPretreatValues = (selectedFilters) =>
  Object.entries(selectedFilters?.[PRETREAT_FILTER_ID] || {})
    .filter(([, active]) => active)
    .map(([value]) => value);

/** Pretreat is DTG-only — embroidery, DTF, sublimation, etc. are excluded. */
export const isDtgItem = (item) => {
  const type = item?.decorationType ?? item?.decorationMethod;
  if (type != null && String(type) === DTG_DECORATION_TYPE) return true;
  const methods = item?.decorationMethods;
  if (Array.isArray(methods)) {
    return methods.some((m) => String(m) === DTG_DECORATION_TYPE);
  }
  return false;
};

export const filterItemsByPretreat = (
  items,
  selectedValues,
  map = DEFAULT_PT_INDICATION,
  { enterpriseId } = {}
) => {
  if (!selectedValues?.length) return items || [];
  const want = new Set(selectedValues.map(String));
  const isGirlTribe = enterpriseId === GIRLTRIBE_ENTERPRISE_ID;
  return (items || []).filter((item) => {
    // Girl Tribe: non-DTG (embroidery, etc.) counts as Pretreat No.
    // Everyone else: pretreat filter is DTG-only.
    if (!isDtgItem(item)) return isGirlTribe && want.has("No");
    const pt = printModeToPretreat(
      resolveItemPrintMode(item, null, { enterpriseId }),
      map
    );
    if (isGirlTribe) {
      // Anything that is not explicitly Yes is Pretreat No.
      return want.has(pt === "Yes" ? "Yes" : "No");
    }
    return pt && want.has(pt);
  });
};

export const stripPretreatFilter = (filters) =>
  (filters || []).filter((f) => f?.id !== PRETREAT_FILTER_ID);
